import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface MenuPhotoSubmissionResponse {
  id: number;
  menu_item_group_id: number;
  menu_item_group_name: string;
  status: 'pending' | 'approved' | 'rejected';
  points_awarded: number;
}

/**
 * Crowd-sourced menu photos: the customer shares a photo of a dish they
 * ordered and earns loyalty points once the restaurant approves it.
 *
 * The upload endpoint is public — the order hash plus the phone number that
 * placed the order is the identity, and the backend enforces the rest (item
 * must be on that order, within a week of it, one photo per ordered item).
 *
 * There is no consumer endpoint for reading a submission's status back, so
 * which items have been sent is remembered per order in localStorage. That
 * only drives what this screen shows; the real guard against a second upload
 * is the backend, which answers 409.
 */
@Injectable({ providedIn: 'root' })
export class MenuPhotoSubmissionService {

  /**
   * Points a photo is worth. The merchant console lets the owner change this,
   * but it is not on the store payload yet — exposing it as a store setting is
   * the outstanding piece of ENG-5292, and this default should read from it
   * once it lands so both sides quote the same number.
   */
  static readonly DEFAULT_POINTS_PER_PHOTO = 25;

  constructor(private http: HttpClient) { }

  submit(storeId: number | string, orderHash: string, menuItemGroupId: number,
         phoneNumber: string, file: File | Blob): Observable<MenuPhotoSubmissionResponse> {
    const formData = new FormData();
    // Filename only matters for the extension the backend logs; it re-encodes.
    formData.append('file', file, 'photo.jpg');
    formData.append('menu_item_group_id', String(menuItemGroupId));
    formData.append('order_id', orderHash);
    formData.append('phone_number', phoneNumber);

    return this.http.post<MenuPhotoSubmissionResponse>(
      `${environment.orderApiBackend}/backend/store/${storeId}/menu_photo_submissions/`,
      formData
    ).pipe(
      tap(() => this.remember(orderHash, menuItemGroupId))
    );
  }

  /** Check the status of all submissions for a given phone number at a store. */
  checkStatuses(storeId: number | string, phoneNumber: string): Observable<MenuPhotoSubmissionResponse[]> {
    return this.http.get<any>(
      `${environment.orderApiBackend}/backend/store/${storeId}/menu_photo_submissions/mine/`,
      { params: { phone_number: phoneNumber } }
    ).pipe(
      map(res => res.submissions || [])
    );
  }

  submittedItemIds(orderHash: string): number[] {
    try {
      const raw = localStorage.getItem(this.storageKey(orderHash));
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  hasSubmitted(orderHash: string, menuItemGroupId: number): boolean {
    return this.submittedItemIds(orderHash).indexOf(menuItemGroupId) !== -1;
  }

  /** Treat a 409 like a success — the photo is already in the review queue */
  remember(orderHash: string, menuItemGroupId: number) {
    const ids = this.submittedItemIds(orderHash);
    if (ids.indexOf(menuItemGroupId) === -1) {
      ids.push(menuItemGroupId);
    }
    try {
      localStorage.setItem(this.storageKey(orderHash), JSON.stringify(ids));
    } catch {
      // Private browsing with storage disabled — the screen just won't
      // remember, and the backend still stops a duplicate upload.
    }
  }

  private storageKey(orderHash: string): string {
    return `menu_photo_submissions_${orderHash}`;
  }
}
